import { Button } from "@dash/form";
import { SectionHeader } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { useTheme } from "@emotion/react";
import React, { useState } from "react";
import { Link } from "react-router-dom-v5-compat";
import { useQueryClient } from "react-query";

import { useClientManager } from "@megaron/react-clients";
import { TicketDetailDto, TicketListItemDto, ticketStatuses, ticketStatusLabels, TicketStatus } from "@megaron/support-contracts";
import { SupportCreateTicketDialog } from "../create/SupportCreateTicketDialog";
import { useSupportTicketEditable } from "../shared/useSupportTicketEditable";
import { getReporterCustomerUuidFromTicket } from "../supportTicketCustomerUtils";
import { SupportTicketMergeDialog } from "./SupportTicketMergeDialog";

const warsawDateTimeFormatter = new Intl.DateTimeFormat("pl-PL", {
  timeZone: "Europe/Warsaw",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

type Props = {
  ticket: TicketDetailDto;
  listPath: string;
};

export const SupportTicketRelatedTickets: React.FC<Props> = ({ ticket, listPath }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const support = useClientManager("support");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mergeTargetTicket, setMergeTargetTicket] = useState<TicketListItemDto | undefined>(undefined);
  const isEditable = useSupportTicketEditable(ticket.status);

  const reporterCustomerUuid = getReporterCustomerUuidFromTicket(ticket);
  const hasContactForRelatedList = Boolean(ticket.reporterEmail) || ticket.ticketCustomers.length > 0;

  const relatedQuery = support.ticketsList().useQuery(
    {
      contactEmail: ticket.reporterEmail,
      customerUuid: reporterCustomerUuid,
      status: [...ticketStatuses],
      limit: 10,
      offset: undefined,
      searchText: undefined,
      priority: undefined,
      category: undefined,
      tags: undefined,
      sort: undefined,
      sortOrder: undefined,
      myTickets: undefined,
      isRead: undefined,
    },
    { enabled: hasContactForRelatedList },
  );

  const relatedTickets = (relatedQuery.data?.items ?? []).filter(
    (t) => String(t.id) !== String(ticket.id),
  );

  if (!hasContactForRelatedList) {
    return null;
  }

  return (
    <>
      <SectionHeader
        isHr
        css={{ h2: { fontSize: "16px", color: theme.colors.primary } }}
        actions={
          relatedTickets.length === 0 ? (
            <Button variant="outline" css={{ fontSize: "12px", padding: "4px 8px" }} onClick={() => setIsDialogOpen(true)}>
              Dodaj
            </Button>
          ) : undefined
        }
      >
        Zgłoszenia
      </SectionHeader>
      <QuerySkeleton query={relatedQuery} height="45px">
        {() =>
          relatedTickets.length > 0 ? (
            <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {relatedTickets.map((item) => (
                <div
                  key={item.id}
                  css={{
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.smallBorderRadius,
                    padding: "0.45rem 0.625rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.125rem",
                    background: "white",
                  }}
                >
                  <div css={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.5rem" }}>
                    <Link
                      to={`${listPath}/${item.id}`}
                      css={{
                        textDecoration: "none",
                        color: "inherit",
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <span css={{ fontSize: "13px", fontWeight: 600, lineHeight: 1.25, display: "block", minWidth: 0 }}>
                        {item.subject}
                      </span>
                      <span
                        css={{
                          display: "block",
                          fontSize: "12px",
                          color: theme.colors.secondaryText,
                          marginTop: "0.125rem",
                        }}
                      >
                        {warsawDateTimeFormatter.format(item.createdAt)}
                      </span>
                    </Link>
                    <div css={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                      <StatusTag status={item.status} />
                      {isEditable && (
                      <Button
                        variant="outline"
                        css={{ fontSize: "11px", padding: "2px 8px", whiteSpace: "nowrap" }}
                        onClick={() => setMergeTargetTicket(item)}
                      >
                        Połącz
                      </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null
        }
      </QuerySkeleton>
      <SupportCreateTicketDialog
        ticket={ticket}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onCreated={() => {
          queryClient.invalidateQueries(relatedQuery.key);
        }}
      />
      {mergeTargetTicket && (
        <SupportTicketMergeDialog
          currentTicket={ticket}
          targetTicket={mergeTargetTicket}
          listPath={listPath}
          onClose={() => setMergeTargetTicket(undefined)}
        />
      )}
    </>
  );
};

const StatusTag: React.FC<{ status: TicketStatus }> = ({ status }) => {
  const theme = useTheme();
  const style =
    status === "new"
      ? { background: theme.colors.primaryLight, color: theme.colors.primary }
      : status === "resolved"
        ? { background: theme.colors.hover, color: theme.colors.success }
        : { background: theme.colors.hover, color: theme.colors.secondaryText };

  return (
    <span
      css={{
        ...style,
        fontSize: "11px",
        fontWeight: 700,
        borderRadius: "999px",
        padding: "2px 8px",
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      {ticketStatusLabels[status]}
    </span>
  );
};
